/* ==========================================================================
   Jane Lushka: cart drawer enhancements (loaded globally, defer)

   1) Keeps the cart-drawer HEADER count "(N)" in sync. The theme's cart.js only
      re-renders the line items, the footer (.js-cart-footer-contents) and the
      cart-icon-bubble on an in-drawer quantity change, NOT the drawer header,
      so the "(N)" went stale. We recompute it from the live quantity inputs
      whenever the drawer subtree changes (qty change / remove / add re-render).

   2) (v1.2 phase 4: the LimeSpot Shop the Look bundle path is gone with
      LimeSpot. The theme's own "Shop the look" rows add through
      assets/jl-size-sheet.js, which renders and opens the drawer itself.)

   3) v1.2 H4: checkout activity state. When a checkout button that opts in
      with [data-jl-checkout] submits its form, it gets the theme's .loading
      spinner, aria-busy and aria-disabled, its label changes to the
      data-checking-out-label text ("Checking out..."), and a second submit is
      blocked. The disabled attribute is never set: a disabled submitter is left
      out of the form data, so the "checkout" field would be lost and the
      shopper would land on /cart instead of the checkout. Everything resets
      when the page comes back from the back-forward cache (pageshow), or
      right away when another script cancels the submit.
      Delegated listeners on document, because cart.js and renderContents()
      replace the drawer markup over AJAX. Any other checkout button (the /cart
      page) can opt in with data-jl-checkout and data-checking-out-label.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- 1) header "(N)" sync -------------------------------------------- */
  function syncDrawerCount() {
    var drawerEl = document.querySelector('[data-cart-drawer]');
    if (!drawerEl) return;
    var countEl = drawerEl.querySelector('[data-cart-drawer-count]');
    if (!countEl) return;
    var inputs = drawerEl.querySelectorAll('.cart-drawer-items [name="updates[]"]');
    var n = 0;
    inputs.forEach(function (input) { n += parseInt(input.value, 10) || 0; });
    var next = n > 0 ? ' (' + n + ')' : '';
    // Only write when it actually changed: avoids a MutationObserver feedback loop.
    if (countEl.textContent !== next) countEl.textContent = next;
  }

  var drawerForObserve = document.querySelector('[data-cart-drawer]');
  if (drawerForObserve && 'MutationObserver' in window) {
    // Observe the stable [data-cart-drawer] node: it survives both partial
    // (cart.js) and full (renderContents) re-renders of its children.
    new MutationObserver(syncDrawerCount).observe(drawerForObserve, {
      childList: true,
      subtree: true
    });
  }

  /* ---- 3) checkout activity state (v1.2 H4) ----------------------------- */
  var CHECKOUT_SELECTOR = 'button[name="checkout"][data-jl-checkout]';
  // Safety net only: if the navigation stalls or is cancelled after the submit
  // went through (a beforeunload prompt, a dropped connection), the button
  // comes back after this long instead of staying stuck on "Checking out...".
  var CHECKOUT_RESET_MS = 20000;
  var lastCheckoutClick = null;

  function isCheckoutBusy(btn) {
    return btn.getAttribute('aria-busy') === 'true';
  }

  function checkoutLabelEl(btn) {
    return btn.querySelector('[data-jl-checkout-label]') || btn;
  }

  function checkoutStatusEl(btn) {
    var scope = btn.closest('.cart-drawer__footer__buttons') || btn.form || btn.parentNode;
    return scope ? scope.querySelector('[data-jl-checkout-status]') : null;
  }

  function setCheckoutBusy(btn) {
    var labelEl = checkoutLabelEl(btn);
    var busyLabel = btn.getAttribute('data-checking-out-label');
    if (!btn.hasAttribute('data-jl-label-original')) {
      btn.setAttribute('data-jl-label-original', labelEl.textContent);
    }
    if (busyLabel) labelEl.textContent = busyLabel;
    btn.classList.add('loading');
    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('aria-disabled', 'true');
    var status = checkoutStatusEl(btn);
    if (status && busyLabel) status.textContent = busyLabel;
    // One timer per button, cleared on reset, so an old timer can never cut a
    // later checkout attempt short.
    clearTimeout(btn._jlCheckoutTimer);
    btn._jlCheckoutTimer = setTimeout(function () { resetCheckout(btn); }, CHECKOUT_RESET_MS);
  }

  function resetCheckout(btn) {
    clearTimeout(btn._jlCheckoutTimer);
    btn._jlCheckoutTimer = null;
    if (btn.hasAttribute('data-jl-label-original')) {
      checkoutLabelEl(btn).textContent = btn.getAttribute('data-jl-label-original');
      btn.removeAttribute('data-jl-label-original');
    }
    btn.classList.remove('loading');
    btn.removeAttribute('aria-busy');
    btn.removeAttribute('aria-disabled');
    var status = checkoutStatusEl(btn);
    if (status) status.textContent = '';
  }

  function resetAllCheckouts() {
    lastCheckoutClick = null;
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (btn) {
      if (isCheckoutBusy(btn) || btn.hasAttribute('data-jl-label-original')) resetCheckout(btn);
    });
  }

  // Click: block a second press while busy (preventing the click of a submit
  // button cancels its submission, also for Enter in a field, which presses the
  // form's first submit button). Otherwise remember the button for browsers
  // without SubmitEvent.submitter (Safari before 15.4).
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var btn = target.closest(CHECKOUT_SELECTOR);
    if (!btn) return;
    if (isCheckoutBusy(btn)) {
      event.preventDefault();
      return;
    }
    lastCheckoutClick = btn;
  });

  // Submit (bubbling, so handlers on the form itself have run first).
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.querySelector) return;
    var btn = event.submitter;
    // (.form, not contains(): a checkout button can sit outside its form with form="...")
    if (btn === undefined && lastCheckoutClick && lastCheckoutClick.form === form) {
      btn = lastCheckoutClick;
    }
    lastCheckoutClick = null;
    if (!btn || !btn.matches || !btn.matches(CHECKOUT_SELECTOR)) return;

    if (isCheckoutBusy(btn)) {
      event.preventDefault();
      return;
    }
    // Another script took over this submit (preventDefault): leave the button alone.
    if (event.defaultPrevented) return;

    setCheckoutBusy(btn);
    // A submit listener that runs after this one (on window, or added to
    // document later, such as an app's terms checkbox) can still cancel the
    // submission. Check once the event has finished and give the button back,
    // so the shopper can press it again instead of being locked out.
    setTimeout(function () {
      if (event.defaultPrevented) resetCheckout(btn);
    }, 0);
  });

  // Back-forward cache: the page comes back exactly as it was left, spinner
  // included. A fresh load (persisted false) needs no reset, and resetting there
  // could clear a checkout pressed before the load event fired.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) resetAllCheckouts();
  });
})();
